/**
 * Touch controls for phones/tablets.
 *
 * Scheme: press-and-hold anywhere in the left drive zone to throttle; drag the
 * held finger horizontally to steer (vertically to pitch when airborne). The
 * right-hand cluster has hold-buttons for BOOST and DRIFT (drift applies the
 * airbrake on the side you're steering toward — the expert line), a tap RESET,
 * and a pause button by the minimap. Pointer Events + touch-action:none, so it
 * also works with pen/touch laptops.
 */

export interface TouchState {
  /** A drive-zone finger is down. */
  driving: boolean;
  steer: number; // -1..1
  pitch: number; // -1..1
  boost: boolean;
  drift: boolean;
}

export class TouchControls {
  readonly state: TouchState = { driving: false, steer: 0, pitch: 0, boost: false, drift: false };
  /** True once any touch input has been used this session (enables merging). */
  used = false;

  private root: HTMLDivElement;
  private drivePointer: number | null = null;
  private anchorX = 0;
  private anchorY = 0;
  private puck: HTMLDivElement;
  private puckDot: HTMLDivElement;
  private respawnPulse = 0;

  constructor(parent: HTMLElement, onPause: () => void) {
    this.root = document.createElement('div');
    this.root.id = 'touch-controls';
    this.root.innerHTML = `
      <style>
        #touch-controls { position: absolute; inset: 0; display: none; z-index: 30; pointer-events: none;
                          font-family: var(--font); -webkit-user-select: none; user-select: none; }
        #touch-controls.show { display: block; }
        #tc-drive { position: absolute; left: 0; top: 18%; bottom: 0; width: 56%;
                    pointer-events: auto; touch-action: none; }
        #tc-puck { position: absolute; width: 108px; height: 108px; margin: -54px 0 0 -54px;
                   border: 1.5px solid rgba(49,200,255,0.4); border-radius: 50%;
                   background: radial-gradient(circle, rgba(49,200,255,0.05), rgba(49,200,255,0.12));
                   display: none; pointer-events: none; }
        #tc-puck-dot { position: absolute; left: 50%; top: 50%; width: 34px; height: 34px; margin: -17px 0 0 -17px;
                       border-radius: 50%; background: rgba(49,200,255,0.55);
                       box-shadow: 0 0 14px rgba(49,200,255,0.8); pointer-events: none; }
        .tc-btn { position: absolute; pointer-events: auto; touch-action: none;
                  display: flex; align-items: center; justify-content: center;
                  border-radius: 50%; border: 1.5px solid var(--line-bright);
                  background: rgba(13,17,26,0.55); color: var(--text-dim);
                  font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
                  -webkit-tap-highlight-color: transparent; }
        .tc-btn.held { border-color: var(--accent); color: #eaf6ff;
                       background: rgba(28,118,160,0.5); box-shadow: 0 0 18px rgba(49,200,255,0.5); }
        #tc-boost { right: 3.5%; bottom: 24%; width: 92px; height: 92px; border-color: rgba(49,200,255,0.65); }
        #tc-drift { right: calc(3.5% + 104px); bottom: 20%; width: 74px; height: 74px; }
        #tc-reset { right: calc(3.5% + 40px); bottom: calc(24% + 108px); width: 56px; height: 56px; font-size: 9px; }
        #tc-pause { right: calc(28px * var(--hud-scale) + 180px * var(--hud-scale) + 14px);
                    top: calc(28px * var(--hud-scale)); width: 44px; height: 44px; font-size: 14px; border-radius: 10px; }
        @media (max-width: 700px) {
          #tc-boost { width: 76px; height: 76px; }
          #tc-drift { right: calc(3.5% + 86px); width: 62px; height: 62px; }
        }
      </style>
      <div id="tc-drive"><div id="tc-puck"><div id="tc-puck-dot"></div></div></div>
      <div class="tc-btn" id="tc-boost">BOOST</div>
      <div class="tc-btn" id="tc-drift">DRIFT</div>
      <div class="tc-btn" id="tc-reset">RESET</div>
      <div class="tc-btn" id="tc-pause">II</div>
    `;
    parent.appendChild(this.root);
    this.puck = this.root.querySelector('#tc-puck')!;
    this.puckDot = this.root.querySelector('#tc-puck-dot')!;

    // ── drive zone: hold = throttle, drag = steer/pitch ──
    const zone = this.root.querySelector<HTMLDivElement>('#tc-drive')!;
    zone.addEventListener('pointerdown', (e) => {
      if (this.drivePointer !== null) return;
      this.used = true;
      this.drivePointer = e.pointerId;
      zone.setPointerCapture(e.pointerId);
      this.anchorX = e.clientX;
      this.anchorY = e.clientY;
      this.state.driving = true;
      this.state.steer = 0;
      this.state.pitch = 0;
      this.puck.style.display = 'block';
      this.puck.style.left = `${e.clientX - zone.getBoundingClientRect().left}px`;
      this.puck.style.top = `${e.clientY - zone.getBoundingClientRect().top}px`;
      this.movePuckDot(0, 0);
      e.preventDefault();
    });
    zone.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.drivePointer) return;
      const dx = e.clientX - this.anchorX;
      const dy = e.clientY - this.anchorY;
      this.state.steer = clamp(dx / 64, -1, 1);
      this.state.pitch = clamp(dy / 84, -1, 1); // drag down = pitch up input handled by caller
      this.movePuckDot(this.state.steer, this.state.pitch);
      e.preventDefault();
    });
    const endDrive = (e: PointerEvent) => {
      if (e.pointerId !== this.drivePointer) return;
      this.drivePointer = null;
      this.state.driving = false;
      this.state.steer = 0;
      this.state.pitch = 0;
      this.puck.style.display = 'none';
    };
    zone.addEventListener('pointerup', endDrive);
    zone.addEventListener('pointercancel', endDrive);

    // ── hold buttons ──
    const holdButton = (id: string, set: (v: boolean) => void) => {
      const el = this.root.querySelector<HTMLDivElement>(`#${id}`)!;
      const down = (e: PointerEvent) => {
        this.used = true;
        set(true);
        el.classList.add('held');
        el.setPointerCapture(e.pointerId);
        e.preventDefault();
      };
      const up = () => {
        set(false);
        el.classList.remove('held');
      };
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
    };
    holdButton('tc-boost', (v) => (this.state.boost = v));
    holdButton('tc-drift', (v) => (this.state.drift = v));

    // ── tap buttons ──
    this.root.querySelector('#tc-reset')!.addEventListener('pointerdown', (e) => {
      this.used = true;
      this.respawnPulse = 0.25; // seconds — a pulse, not a hold, so it can't re-trigger
      (e.target as HTMLElement).classList.add('held');
      setTimeout(() => (e.target as HTMLElement).classList.remove('held'), 220);
      e.preventDefault();
    });
    this.root.querySelector('#tc-pause')!.addEventListener('pointerdown', (e) => {
      this.used = true;
      onPause();
      e.preventDefault();
    });
  }

  private movePuckDot(sx: number, sy: number): void {
    this.puckDot.style.transform = `translate(${sx * 34}px, ${sy * 34}px)`;
  }

  /** Consume the respawn pulse (called once per sampled frame with dt). */
  consumeRespawn(dt: number): boolean {
    if (this.respawnPulse > 0) {
      this.respawnPulse -= dt;
      return true;
    }
    return false;
  }

  setVisible(v: boolean): void {
    this.root.classList.toggle('show', v);
    if (!v) {
      this.drivePointer = null;
      this.state.driving = false;
      this.state.steer = 0;
      this.state.pitch = 0;
      this.state.boost = false;
      this.state.drift = false;
      this.puck.style.display = 'none';
      this.root.querySelectorAll('.tc-btn').forEach((b) => b.classList.remove('held'));
    }
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function isTouchCapable(): boolean {
  return (
    (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  );
}
